import React, { useState, useEffect } from 'react';
import {
  useNavigate,
  Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { trackLogin } from '../services/analytics';
import SEO from './SEO.jsx';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Button,
  Link,
  Icon,
  Image,
  IconButton,
  Alert,
  Checkbox,
  Field,
} from '@chakra-ui/react';
import { InputGroup, InputLeftElement, InputRightElement } from '@/components/ui/input-group';
import {
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa';

const Login = () => {
  const navigate = useNavigate();
  const { login, error: authError, clearError } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Limpiar error del AuthContext al montar el componente
  useEffect(() => {
    if (authError) {
      clearError();
    }
  }, []); // Solo al montar

  const validateForm = () => {
    const newErrors = {};

    // Validar email
    if (!formData.email) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }

    // Validar contraseña
    if (!formData.password) {
      newErrors.password = 'La contraseña es obligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (authError) {
      clearError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({}); // Limpiar errores previos
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        trackLogin('email');
        setIsLoading(false);
        // AuthRoute muestra LoadingScreen (splash) y luego redirige al perfil
      } else {
        // Mostrar el error específico del backend
        setErrors({ submit: result.error || 'Email o contraseña incorrectos. Por favor, inténtalo de nuevo.' });
        setIsLoading(false);
        // Hacer scroll hacia arriba para mostrar el error
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error en el login:', error);
      // Intentar extraer el mensaje de error del servidor
      const errorMessage = error.response?.data?.message || 'Error al conectar con el servidor. Por favor, inténtalo de nuevo.';
      setErrors({ submit: errorMessage });
      setIsLoading(false);
      // Hacer scroll hacia arriba para mostrar el error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEO title="Iniciar Sesión" path="/login" noIndex />
      <Box minH="100vh" bg="linear-gradient(135deg, #f5f3ff 0%, #ffffff 45%, #fffbeb 100%)">
        {/* Header simple */}
        <Box bg="white" boxShadow="sm" py={5}>
          <Container maxW="7xl">
            <HStack justify="space-between">
              <HStack 
                gap={3}
                cursor="pointer"
                onClick={() => navigate('/')}
              >
                <Image 
                  src="/logo y texto cadencya.png" 
                  alt="Cadencya" 
                  h={{ base: "55px", md: "70px" }}
                  w="auto"
                />
              </HStack>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/registro')}
                fontWeight="600"
              >
                Crear cuenta
              </Button>
            </HStack>
          </Container>
        </Box>

        {/* Contenido principal */}
        <Container maxW="md" py={{ base: 8, md: 16 }}>
          <VStack gap={{ base: 8, md: 12 }}>
            {/* Formulario de login */}
            <Box
              bg="white"
              p={{ base: 8, md: 10 }}
              rounded="3xl"
              boxShadow="xl"
              w="full"
            >
              <VStack gap={6} mb={8}>
                <Image
                  src="/capirote-amarillo.png"
                  alt="Bienvenido"
                  maxW="120px"
                />
                <VStack gap={2}>
                  <Heading size="xl" textAlign="center">
                    ¡Bienvenido de vuelta!
                  </Heading>
                  <Text color="gray.600" textAlign="center">
                    Accede a tu cuenta
                  </Text>
                </VStack>
              </VStack>

              <form onSubmit={handleSubmit}>
                <VStack gap={5}>
                  {/* Error general */}
                  {(errors.submit || authError) && (
                    <Alert.Root status="error" rounded="xl">
                      <Alert.Indicator />
                      {errors.submit || authError}
                    </Alert.Root>
                  )}

                  {/* Email */}
                  <Field.Root invalid={!!errors.email}>
                    <Field.Label fontWeight="600">Email</Field.Label>
                    <InputGroup size="lg">
                      <InputLeftElement>
                        <Icon as={FaEnvelope} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        type="email"
                        placeholder="tu@email.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        rounded="xl"
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{ borderColor: 'purple.500', bg: 'white' }}
                      />
                    </InputGroup>
                    <Field.ErrorText>{errors.email}</Field.ErrorText>
                  </Field.Root>

                  {/* Contraseña */}
                  <Field.Root invalid={!!errors.password}>
                    <Field.Label fontWeight="600">Contraseña</Field.Label>
                    <InputGroup size="lg">
                      <InputLeftElement>
                        <Icon as={FaLock} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Tu contraseña"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        rounded="xl"
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{ borderColor: 'purple.500', bg: 'white' }}
                      />
                      <InputRightElement>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}><Icon as={showPassword ? FaEyeSlash : FaEye} /></IconButton>
                      </InputRightElement>
                    </InputGroup>
                    <Field.ErrorText>{errors.password}</Field.ErrorText>
                  </Field.Root>

                  {/* Opciones adicionales */}
                  <HStack w="full" justify="space-between" align="center">
                    <Checkbox.Root
                      onCheckedChange={(details) => handleInputChange('rememberMe', details.checked === true)}
                      colorPalette="purple"
                      size="lg"
                      checked={formData.rememberMe}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" color="gray.600">Recordarme</Text>
                    </Checkbox.Label></Checkbox.Root>
                    
                    <Link
                      as={RouterLink}
                      to="/forgot-password"
                      color="purple.600"
                      fontSize="sm"
                      fontWeight="600"
                      _hover={{ textDecoration: "underline" }}
                    >
                      ¿Olvidaste tu contraseña?
                    </Link>
                  </HStack>

                  {/* Botón de login */}
                  <Button
                    type="submit"
                    bg="purple.600"
                    color="white"
                    size="lg"
                    w="full"
                    h="14"
                    rounded="full"
                    loading={isLoading}
                    loadingText="Iniciando sesión..."
                    fontSize="lg"
                    fontWeight="700"
                    _hover={{ 
                      bg: 'purple.700',
                      transform: 'scale(1.02)',
                    }}
                    _active={{ transform: 'scale(0.98)' }}
                  >
                    Iniciar sesión
                  </Button>

                  {/* Link a registro */}
                  <Text fontSize="sm" color="gray.600" textAlign="center" pt={2}>
                    ¿No tienes una cuenta?{' '}
                    <Link 
                      as={RouterLink}
                      to="/registro"
                      color="purple.600" 
                      fontWeight="600"
                      _hover={{ textDecoration: "underline" }}
                    >
                      Crear cuenta gratis
                    </Link>
                  </Text>
                </VStack>
              </form>
            </Box>
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default Login;
